import { BusMessage, MessageFactory, PrimitiveShape, calcCollisionShapeByteLength, collisionShapeSerialization, UID_BYTES_PER_ELEMENT, BOOLEAN_BYTES_PER_ELEMENT, NUMBER3_BYTES_PER_ELEMENT, NUMBER16_BYTES_PER_ELEMENT } from "flavr-common";
/**
 * Message that creates a collision shape for the model specified by uid. You have to specify a shape that is used to
 * perform the raycast or intersection test. Try to aim for simpler shapes as these will increase performance.
 */
export class CreateCollisionShapeMessage extends BusMessage {
    get byteLength() {
        return CreateCollisionShapeMessage.START_BYTE_LENGTH + calcCollisionShapeByteLength(this.shape);
    }
    constructor(shape = PrimitiveShape.Box, uid = 0, isSelectable = false, isTrigger = false) {
        super();
        this.shape = shape;
        this.uid = uid;
        this.isSelectable = isSelectable;
        this.isTrigger = isTrigger;
    }
    serialization(stream) {
        this.shape = collisionShapeSerialization(stream, this.shape);
        this.uid = stream.uid(this.uid);
        this.isSelectable = stream.boolean(this.isSelectable);
        this.isTrigger = stream.boolean(this.isTrigger);
    }
    static is(msg) {
        return msg.type === CreateCollisionShapeMessage.typestatic;
    }
}
CreateCollisionShapeMessage.typestatic = "CreateCollisionShapeMessage";
CreateCollisionShapeMessage.subjectstatic = "selection";
CreateCollisionShapeMessage.START_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    BOOLEAN_BYTES_PER_ELEMENT + // isSelectable
    BOOLEAN_BYTES_PER_ELEMENT; // isTrigger
MessageFactory.register(CreateCollisionShapeMessage);
/**
 * Message that removes a collision shape for the model specified by uid
 */
export class RemoveCollisionShapeMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(uid = 0) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RemoveCollisionShapeMessage.typestatic;
    }
}
RemoveCollisionShapeMessage.typestatic = "RemoveCollisionShapeMessage";
RemoveCollisionShapeMessage.subjectstatic = "selection";
MessageFactory.register(RemoveCollisionShapeMessage);
/**
 * Message that removes all collision shapes from the scene.
 */
export class RemoveAllCollisionShapesMessage extends BusMessage {
    serialization() { }
    static is(msg) {
        return msg.type === RemoveAllCollisionShapesMessage.typestatic;
    }
}
RemoveAllCollisionShapesMessage.typestatic = "RemoveAllCollisionShapesMessage";
RemoveAllCollisionShapesMessage.subjectstatic = "selection";
MessageFactory.register(RemoveAllCollisionShapesMessage);
/**
 * Message to specify the local transformation of the collision shape relative to its model
 */
export class UpdateCollisionShapeLocalTransformMessage extends BusMessage {
    get byteLength() {
        return UpdateCollisionShapeLocalTransformMessage.START_BYTE_LENGTH;
    }
    constructor(uid = 0, localTransform) {
        super();
        this.uid = uid;
        this.localTransform = localTransform;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.localTransform = stream.number16(this.localTransform);
    }
    static is(msg) {
        return msg.type === UpdateCollisionShapeLocalTransformMessage.typestatic;
    }
}
UpdateCollisionShapeLocalTransformMessage.typestatic = "UpdateCollisionShapeLocalTransformMessage";
UpdateCollisionShapeLocalTransformMessage.subjectstatic = "selection";
UpdateCollisionShapeLocalTransformMessage.START_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    NUMBER16_BYTES_PER_ELEMENT; // localTransform
MessageFactory.register(UpdateCollisionShapeLocalTransformMessage);
/**
 * Request to get the local transform of a collision shape. Returns a ResponseCollisionShapeLocalTransformMessage
 */
export class RequestCollisionShapeLocalTransformMessage extends BusMessage {
    get byteLength() {
        return RequestCollisionShapeLocalTransformMessage.BYTE_LENGTH;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RequestCollisionShapeLocalTransformMessage.typestatic;
    }
}
RequestCollisionShapeLocalTransformMessage.typestatic = "RequestCollisionShapeLocalTransformMessage";
RequestCollisionShapeLocalTransformMessage.subjectstatic = "selection";
RequestCollisionShapeLocalTransformMessage.BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
MessageFactory.register(RequestCollisionShapeLocalTransformMessage);
/**
 * Response providing a local transform for a collision shape. Send as an answer to a RequestCollisionShapeLocalTransformMessage
 */
export class ResponseCollisionShapeLocalTransformMessage extends BusMessage {
    get byteLength() {
        return ResponseCollisionShapeLocalTransformMessage.BYTE_LENGTH;
    }
    constructor(localTransform) {
        super();
        this.localTransform = localTransform;
    }
    serialization(stream) {
        this.localTransform = stream.number16(this.localTransform);
    }
    static is(msg) {
        return msg.type === ResponseCollisionShapeLocalTransformMessage.typestatic;
    }
}
ResponseCollisionShapeLocalTransformMessage.typestatic = "ResponseCollisionShapeLocalTransformMessage";
ResponseCollisionShapeLocalTransformMessage.subjectstatic = "selection";
ResponseCollisionShapeLocalTransformMessage.BYTE_LENGTH = NUMBER16_BYTES_PER_ELEMENT; // localTransform
MessageFactory.register(ResponseCollisionShapeLocalTransformMessage);
/**
 * Message that enables or disables a collision shape. A disabled collision shape is not used when testing for
 * intersections.
 */
export class UpdateCollisionShapeSelectableStateMessage extends BusMessage {
    get byteLength() {
        return UpdateCollisionShapeSelectableStateMessage.BASE_BYTE_LENGTH + (this.isTrigger !== undefined ? BOOLEAN_BYTES_PER_ELEMENT : 0);
    }
    /**
     * @param isTrigger leave undefined to only change isSelectable and keep whatever isTrigger currently is
     */
    constructor(uid, isSelectable, isTrigger) {
        super();
        this.uid = uid;
        this.isSelectable = isSelectable;
        this.isTrigger = isTrigger;
    }
    serialization(stream) {
        var _a;
        this.uid = stream.uid(this.uid);
        this.isSelectable = stream.boolean(this.isSelectable);
        const hasIsTrigger = stream.boolean(this.isTrigger !== undefined);
        this.isTrigger = hasIsTrigger ? stream.boolean((_a = this.isTrigger) !== null && _a !== void 0 ? _a : false) : undefined;
    }
    static is(msg) {
        return msg.type === UpdateCollisionShapeSelectableStateMessage.typestatic;
    }
}
UpdateCollisionShapeSelectableStateMessage.typestatic = "UpdateCollisionShapeSelectableStateMessage";
UpdateCollisionShapeSelectableStateMessage.subjectstatic = "selection";
UpdateCollisionShapeSelectableStateMessage.BASE_BYTE_LENGTH = (UID_BYTES_PER_ELEMENT + // uid
    BOOLEAN_BYTES_PER_ELEMENT + // isSelectable
    BOOLEAN_BYTES_PER_ELEMENT // hasIsTrigger
);
MessageFactory.register(UpdateCollisionShapeSelectableStateMessage);
/**
 * Request the already-built vertex/index data of a collision shape's geometry. Unlike re-deriving geometry from a
 * model's own (possibly stale) CollisionFacet mirror, this returns whatever CollisionWorld actually has committed -
 * which for a compound shape includes elements CompoundColliderManager derived live from descendant CollisionFacets
 * (CompoundColliderManager mutates the compound facet's shape in place rather than via setFacet, to avoid an
 * infinite rebuild loop through CollisionModule's own facet handler - so other modules' facet mirrors, like the
 * renderer's, never see those resolved elements and can't rebuild the same geometry themselves).
 */
export class RequestCollisionShapeGeometryMessage extends BusMessage {
    get byteLength() {
        return RequestCollisionShapeGeometryMessage.BYTE_LENGTH;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RequestCollisionShapeGeometryMessage.typestatic;
    }
}
RequestCollisionShapeGeometryMessage.typestatic = "RequestCollisionShapeGeometryMessage";
RequestCollisionShapeGeometryMessage.subjectstatic = "selection";
RequestCollisionShapeGeometryMessage.BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
MessageFactory.register(RequestCollisionShapeGeometryMessage);
/**
 * Response providing the vertex/index data of a collision shape's already-built geometry, as an answer to a
 * RequestCollisionShapeGeometryMessage. index is empty for non-indexed geometry.
 */
export class ResponseCollisionShapeGeometryMessage extends BusMessage {
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT * 2 +
            this.vertices.length * Float32Array.BYTES_PER_ELEMENT +
            this.index.length * Float32Array.BYTES_PER_ELEMENT);
    }
    constructor(vertices, index) {
        super();
        this.vertices = vertices;
        this.index = index;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.vertices = [];
            this.index = [];
        }
        let length = stream.uint32(this.vertices.length);
        this.vertices = stream.numberFloat32Array(this.vertices, length);
        length = stream.uint32(this.index.length);
        this.index = stream.numberFloat32Array(this.index, length);
    }
    static is(msg) {
        return msg.type === ResponseCollisionShapeGeometryMessage.typestatic;
    }
}
ResponseCollisionShapeGeometryMessage.typestatic = "ResponseCollisionShapeGeometryMessage";
ResponseCollisionShapeGeometryMessage.subjectstatic = "selection";
MessageFactory.register(ResponseCollisionShapeGeometryMessage);
/**
 * Message that is broadcasts all collision shapes that entered a collision since the last frame
 */
export class OnEnterContactsMessage extends BusMessage {
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // length
            this.contacts.length * (2 * UID_BYTES_PER_ELEMENT) // { collisionShapeAUID, collisionShapeBUID }
        );
    }
    constructor(contacts) {
        super();
        this.contacts = contacts;
    }
    serialization(stream) {
        if (stream.isReading)
            this.contacts = [];
        const length = stream.uint32(this.contacts.length);
        for (let i = 0; i < length; ++i) {
            if (stream.isReading)
                this.contacts[i] = {};
            const contact = this.contacts[i];
            contact.collisionShapeAUID = stream.uid(contact.collisionShapeAUID);
            contact.collisionShapeBUID = stream.uid(contact.collisionShapeBUID);
        }
    }
    static is(msg) {
        return msg.type === OnEnterContactsMessage.typestatic;
    }
}
OnEnterContactsMessage.typestatic = "OnEnterContactsMessage";
OnEnterContactsMessage.subjectstatic = "collision";
MessageFactory.register(OnEnterContactsMessage);
/**
 * Message that is broadcasts all collision shapes that exited a collision since the last frame
 */
export class OnLeaveContactsMessage extends BusMessage {
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // length
            this.contacts.length * (2 * UID_BYTES_PER_ELEMENT) // { collisionShapeAUID, collisionShapeBUID }
        );
    }
    constructor(contacts) {
        super();
        this.contacts = contacts;
    }
    serialization(stream) {
        if (stream.isReading)
            this.contacts = [];
        const length = stream.uint32(this.contacts.length);
        for (let i = 0; i < length; ++i) {
            if (stream.isReading)
                this.contacts[i] = {};
            const contact = this.contacts[i];
            contact.collisionShapeAUID = stream.uid(contact.collisionShapeAUID);
            contact.collisionShapeBUID = stream.uid(contact.collisionShapeBUID);
        }
    }
    static is(msg) {
        return msg.type === OnLeaveContactsMessage.typestatic;
    }
}
OnLeaveContactsMessage.typestatic = "OnLeaveContactsMessage";
OnLeaveContactsMessage.subjectstatic = "collision";
MessageFactory.register(OnLeaveContactsMessage);
/**
 * Request the axis aligned bounding box of a collision shape
 */
export class RequestCollisionShapeAABBMessage extends BusMessage {
    get byteLength() {
        return RequestCollisionShapeAABBMessage.BYTE_LENGTH;
    }
    constructor(uid, ignoreRotation = false) {
        super();
        this.uid = uid;
        this.ignoreRotation = ignoreRotation;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.ignoreRotation = stream.boolean(this.ignoreRotation);
    }
    static is(msg) {
        return msg.type === RequestCollisionShapeAABBMessage.typestatic;
    }
}
RequestCollisionShapeAABBMessage.typestatic = "RequestCollisionShapeAABBMessage";
RequestCollisionShapeAABBMessage.subjectstatic = "selection";
RequestCollisionShapeAABBMessage.BYTE_LENGTH = (UID_BYTES_PER_ELEMENT + // uid
    BOOLEAN_BYTES_PER_ELEMENT);
MessageFactory.register(RequestCollisionShapeAABBMessage);
/**
 * Response providing a local transform for a collision shape. Send as an answer to a RequestCollisionShapeLocalTransformMessage
 */
export class ResponseCollisionShapeAABBMessage extends BusMessage {
    get byteLength() {
        return ResponseCollisionShapeAABBMessage.BYTE_LENGTH;
    }
    constructor(min, max) {
        super();
        this.min = min;
        this.max = max;
    }
    serialization(stream) {
        this.min = stream.number3(this.min);
        this.max = stream.number3(this.max);
    }
    static is(msg) {
        return msg.type === ResponseCollisionShapeAABBMessage.typestatic;
    }
}
ResponseCollisionShapeAABBMessage.typestatic = "ResponseCollisionShapeAABBMessage";
ResponseCollisionShapeAABBMessage.subjectstatic = "selection";
ResponseCollisionShapeAABBMessage.BYTE_LENGTH = NUMBER3_BYTES_PER_ELEMENT * 2; // min, max
MessageFactory.register(ResponseCollisionShapeAABBMessage);
